import type { Ref } from 'vue'
import type { BaseItem } from '~/models/Content/BaseItem'
import type { Task } from '~/models/AssignTask'
import { computed } from 'vue'
import { useQuery } from '@tanstack/vue-query'
import useContentApi from '~/api/contentApi'

export default function useTaskLocations(tasks: Ref<Task[] | undefined>) {
  const { findContents } = useContentApi()

  const locationIds = computed(() => {
    const ids = tasks.value?.flatMap(task => task.locationIds || []) || []
    return [...new Set(ids)].sort()
  })

  const { data: locations, isLoading } = useQuery({
    enabled: computed(() => locationIds.value.length > 0),
    queryKey: computed(() => ['task-locations', { ids: locationIds.value }]),
    queryFn: () => findContents({
      locationIdCriterion: locationIds.value,
    }, locationIds.value.length),
    staleTime: Infinity
  })

  const enrichedTasks = computed((): (Task & { content?: BaseItem })[] => {
    if (!tasks.value)
      return []

    return tasks.value.map((task) => {
      const firstLocationId = task.locationIds?.[0]
      if (!firstLocationId)
        return { ...task, content: undefined } // Explicitly add content property

      const content = locations.value?.find(location =>
        location.locationId === firstLocationId)

      return { ...task, content }
    }).filter(task => task.content || task.favoriteList)
  })

  return {
    enrichedTasks,
    isLoadingLocations: isLoading
  }
}
